import React, { useCallback, useContext, useMemo, useState } from 'react';
import { DataRoomShareableLink } from 'models/ShareableLink';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ActivateSecureLinkModal from '../components/ActivateSecureLinkModal/ActivateSecureLinkModal.component';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { getIsArrayEmpty } from 'utils';
import EditShareableLinkContext from 'router/subrouters/DataRoom/providers/EditShareableLink/EditShareableLink.context';

export type UseShareableLinkActionProps = {
  link: DataRoomShareableLink;
  updateShareableLink?: (link: DataRoomShareableLink) => void;
  onAfterToggleState: (linkBeforeToggle: DataRoomShareableLink) => void;
};

const useChangeShareableLinkState = (props: UseShareableLinkActionProps) => {
  const { link, updateShareableLink, onAfterToggleState } = props;

  const { isActive, fileIds } = link;

  const [isLoading, setIsLoading] = useState(false);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const { doesntHaveAccess } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);
  const { openFilesSelectForLink } = useContext(EditShareableLinkContext);

  const isEmptySecureLink = getIsArrayEmpty(fileIds);

  const changeStateApi = useCallback(
    async (link: DataRoomShareableLink) => {
      const { _id, isActive } = link;

      try {
        setIsLoading(true);

        const { data } =
          await dataRoomApi.shareableLinks.changeShareableLinkState(
            _id,
            !isActive,
          );

        updateShareableLink?.({ ...link, ...data });
        onAfterToggleState?.(link);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [onAfterToggleState, popServerError, updateShareableLink],
  );

  const changeState = useCallback(async () => {
    if (doesntHaveAccess) return;

    if (!isActive && isEmptySecureLink) {
      modal?.open();
      return;
    }

    await changeStateApi(link);
  }, [
    doesntHaveAccess,
    isActive,
    isEmptySecureLink,
    link,
    modal,
    changeStateApi,
  ]);

  const onActivate = useCallback(
    () => changeStateApi(link),
    [link, changeStateApi],
  );

  const onAddFiles = useCallback(
    () => openFilesSelectForLink(link, changeStateApi),
    [link, changeStateApi, openFilesSelectForLink],
  );

  const activateSecureLinkModal = useMemo(
    () => (
      <ActivateSecureLinkModal
        ref={modalRef}
        onActivate={onActivate}
        onAddFiles={onAddFiles}
      />
    ),
    [modalRef, onActivate, onAddFiles],
  );

  return {
    changeState,
    isActive,
    isChangeStateLoading: isLoading,
    isChangeStateDisabled: isLoading || doesntHaveAccess,
    activateSecureLinkModal,
  };
};

export default useChangeShareableLinkState;
